import { FormField, FormSection, Input, Select, Switch } from './ui'

export interface IdPFormState {
  name: string
  issuer_url: string
  client_id: string
  client_secret: string
  allowed_groups: string
  is_default: boolean
}

export function emptyIdPForm(): IdPFormState {
  return {
    name: '',
    issuer_url: '',
    client_id: '',
    client_secret: '',
    allowed_groups: '',
    is_default: true,
  }
}

/** Builds the API payload from form state, omitting client_secret when blank
 *  so an edit doesn't clobber the stored secret (write-only field). */
export function buildIdPPayload(form: IdPFormState) {
  return {
    name: form.name,
    idp_type: 'oidc',
    issuer_url: form.issuer_url,
    client_id: form.client_id,
    ...(form.client_secret ? { client_secret: form.client_secret } : {}),
    allowed_groups: form.allowed_groups
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean),
    is_default: form.is_default,
  }
}

interface Preset {
  label: string
  issuerHint: string
  issuerPlaceholder: string
}

// Issuer-URL shape hints for the wizard's provider picker (ADR-010 §5/§10)
// — informational only, never validated against; any OIDC-compliant issuer
// is accepted.
const PRESETS: Record<string, Preset> = {
  okta: {
    label: 'Okta',
    issuerHint: 'Found in Okta admin under Security → API → Authorization Servers.',
    issuerPlaceholder: 'https://your-org.okta.com',
  },
  entra: {
    label: 'Microsoft Entra ID',
    issuerHint: 'Found in Entra admin under App registrations → Endpoints.',
    issuerPlaceholder: 'https://login.microsoftonline.com/<tenant-id>/v2.0',
  },
  keycloak: {
    label: 'Keycloak',
    issuerHint: 'Your realm’s OIDC issuer, from Realm Settings → General → Endpoints.',
    issuerPlaceholder: 'https://keycloak.example.com/realms/<realm>',
  },
  custom: {
    label: 'Custom OIDC',
    issuerHint: 'Any OIDC-compliant issuer URL that publishes /.well-known/openid-configuration.',
    issuerPlaceholder: 'https://idp.example.com',
  },
}

export const PRESET_KEYS = Object.keys(PRESETS) as Array<keyof typeof PRESETS>

/** The redirect_uri / post-logout URI an admin must paste into their IdP's
 *  application settings — computed the same way the backend does (ADR-010 §2). */
export function computeCallbackURIs(tenantHost: string) {
  return {
    redirectURI: `https://${tenantHost}/auth/oidc/callback`,
    postLogoutURI: `https://${tenantHost}/auth/login`,
    /** Required in addition to redirectURI if the IdP is also tested with
     *  "Test Sign-In" (see AdminIdentityProviderPage) — some IdPs enforce an
     *  exact redirect_uri match, so both must be registered up front. */
    testRedirectURI: `https://${tenantHost}/auth/oidc/test/callback`,
  }
}

export function IdPFormFields({
  form,
  setForm,
  editMode,
  preset,
  onPresetChange,
}: {
  form: IdPFormState
  setForm: (f: IdPFormState) => void
  editMode: boolean
  preset: keyof typeof PRESETS
  onPresetChange: (p: keyof typeof PRESETS) => void
}) {
  const p = PRESETS[preset]
  return (
    <div className="space-y-6">
      <FormSection title="Provider" description="Any OIDC-compliant identity provider works.">
        <FormField label="Provider type" htmlFor="idp-preset">
          <Select
            id="idp-preset"
            value={preset}
            onChange={(e) => onPresetChange(e.target.value as keyof typeof PRESETS)}
          >
            {PRESET_KEYS.map((key) => (
              <option key={key} value={key}>
                {PRESETS[key].label}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Name *" htmlFor="idp-name">
          <Input
            id="idp-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Acme Corp Okta"
          />
        </FormField>
        <FormField label="Issuer URL *" htmlFor="idp-issuer" hint={p.issuerHint}>
          <Input
            id="idp-issuer"
            required
            value={form.issuer_url}
            onChange={(e) => setForm({ ...form, issuer_url: e.target.value })}
            placeholder={p.issuerPlaceholder}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Client ID *" htmlFor="idp-client-id">
            <Input
              id="idp-client-id"
              required
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            />
          </FormField>
          <FormField
            label={editMode ? 'Client Secret' : 'Client Secret *'}
            htmlFor="idp-client-secret"
            hint={editMode ? 'Leave blank to keep the current secret unchanged.' : undefined}
          >
            <Input
              id="idp-client-secret"
              type="password"
              required={!editMode}
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={editMode ? '•••• (unchanged)' : ''}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection title="Access" description="Optional restriction and default status.">
        <FormField
          label="Allowed Groups"
          htmlFor="idp-groups"
          hint="Comma-separated. Leave blank to allow any authenticated user through this IdP."
        >
          <Input
            id="idp-groups"
            value={form.allowed_groups}
            onChange={(e) => setForm({ ...form, allowed_groups: e.target.value })}
            placeholder="engineering, it-admins"
          />
        </FormField>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-foreground">Default provider</p>
            <p className="text-xs text-muted">
              The provider used for sign-in on this tenant. Only one provider can be default.
            </p>
          </div>
          <Switch
            checked={form.is_default}
            onCheckedChange={(checked) => setForm({ ...form, is_default: checked })}
          />
        </div>
      </FormSection>
    </div>
  )
}
